import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useState } from 'react';
import axios from 'axios';
import {
  FileText,
  CheckCircle,
  Users,
  Phone,
  ArrowRight,
  Calculator,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Star,
  Briefcase,
  Home,
  TrendingUp,
  Globe,
  Building2,
  Award
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ItrFilingPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    service_type: 'ITR Filing',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/contact`, {
        ...formData,
        message: `Callback request for ${formData.service_type}`,
        source_page: 'ITR Filing'
      });
      setIsSubmitted(true);
    } catch (err) {
      console.error('Form submission error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const whoShouldFile = [
    { icon: Users, title: 'Salaried Individuals', desc: 'Income exceeding ₹2.5 lakhs' },
    { icon: Briefcase, title: 'Freelancers', desc: 'Professionals with taxable income' },
    { icon: Building2, title: 'Business Owners', desc: 'All businesses regardless of profit' },
    { icon: Globe, title: 'NRIs', desc: 'Income from India sources' },
    { icon: Home, title: 'Property Owners', desc: 'Rental income earners' },
    { icon: TrendingUp, title: 'Investors', desc: 'Capital gains from investments' },
  ];

  const newSlabs = [
    { range: '₹0 – ₹3L', rate: 'Nil' },
    { range: '₹3L – ₹6L', rate: '5%' },
    { range: '₹6L – ₹9L', rate: '10%' },
    { range: '₹9L – ₹12L', rate: '15%' },
    { range: '₹12L – ₹15L', rate: '20%' },
    { range: 'Above ₹15L', rate: '30%' },
  ];

  const oldSlabs = [
    { range: '₹0 – ₹2.5L', rate: 'Nil' },
    { range: '₹2.5L – ₹5L', rate: '5%' },
    { range: '₹5L – ₹10L', rate: '20%' },
    { range: 'Above ₹10L', rate: '30%' },
  ];

  const documents = [
    'PAN Card & Aadhaar Card',
    'Form 16 from Employer',
    'Bank Account Details',
    'Form 26AS / AIS',
    'Investment Proofs (80C, 80D)',
    'Rent Receipts (if applicable)',
    'Capital Gain Statements',
    'Previous Year ITR',
  ];

  const faqs = [
    { q: 'What is Income Tax Return (ITR)?', a: 'ITR is a formal statement filed with the Income Tax Department declaring your total income, taxes paid, deductions claimed, and tax liability or refund.' },
    { q: 'Is ITR filing mandatory even if taxes are paid?', a: 'Yes, if your income exceeds the basic exemption limit. ITR serves as official proof for loans, visas, and helps claim refunds.' },
    { q: 'Which tax regime should I choose?', a: 'New Regime offers lower rates but no deductions. Old Regime allows deductions under 80C, 80D, HRA. Choose based on your deductions.' },
    { q: 'What is the penalty for late filing?', a: 'Late fee of ₹1,000 to ₹5,000 depending on income, plus 1% interest per month on unpaid taxes.' },
    { q: 'Can I file ITR if income is below taxable limit?', a: 'Yes, voluntarily filing helps claim TDS refunds, maintain financial records, and serves as income proof.' },
  ];

  const stats = [
    { value: '8000+', label: 'Happy Customers', icon: Users },
    { value: '12000+', label: 'ITR Filed', icon: FileText },
    { value: '20+', label: 'Expert CAs', icon: Award },
    { value: '4.9', label: 'Rating', icon: Star },
  ];

  return (
    <div data-testid="itr-filing-page" className="min-h-screen pt-20">
      {/* Hero */}
      <section data-testid="itr-hero" className="relative py-16 md:py-24 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div className="space-y-6">
              <div className="flex items-center gap-2">
                {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 text-yellow-400 fill-yellow-400" />)}
                <span className="text-slate-400 text-sm">4.9 (468+ ratings)</span>
              </div>
              <h1 className="font-heading font-bold text-4xl sm:text-5xl text-white leading-tight">
                Income Tax Return <span className="text-gradient">Filing Services</span>
              </h1>
              <p className="text-lg text-slate-400">
                Expert ITR filing for salaried individuals, freelancers, business owners, and NRIs. 
                Quick, accurate filing with maximum refunds.
              </p>
              <div className="flex flex-wrap gap-4">
                {['Quick Process', 'Expert CA Review', 'Max Refunds'].map(item => (
                  <div key={item} className="flex items-center gap-2 text-slate-300">
                    <CheckCircle className="w-5 h-5 text-blue-400" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
              <a href="tel:+919717705640">
                <Button data-testid="hero-call" className="bg-blue-500 hover:bg-blue-600 text-white px-8 py-6 text-lg">
                  <Phone className="mr-2 w-5 h-5" />Call: +91-9717705640
                </Button>
              </a>
            </div>
            <div id="contact-form" className="bg-navy-800 border border-white/10 rounded-2xl p-8">
              {isSubmitted ? (
                <div data-testid="success" className="text-center py-8">
                  <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
                  <h3 className="font-heading font-bold text-xl text-white mb-2">Request Received!</h3>
                  <p className="text-slate-400">Our expert will call you shortly.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-testid="callback-form" className="space-y-4">
                  <h3 className="font-heading font-bold text-xl text-white mb-6">Get Free ITR Consultation</h3>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Your Name</Label>
                    <Input name="name" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} required placeholder="Enter your name" className="bg-navy-900 border-white/10 text-white" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Contact Number</Label>
                    <Input name="phone" type="tel" value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} required placeholder="+91 XXXXX XXXXX" className="bg-navy-900 border-white/10 text-white" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Email Address</Label>
                    <Input name="email" type="email" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} required placeholder="your@email.com" className="bg-navy-900 border-white/10 text-white" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Select Service</Label>
                    <Select onValueChange={v => setFormData({...formData, service_type: v})} defaultValue="ITR Filing">
                      <SelectTrigger className="bg-navy-900 border-white/10 text-white"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-navy-800 border-white/10">
                        {['ITR Filing', 'ITR - Salaried', 'ITR - Business', 'ITR - Capital Gains', 'ITR - NRI'].map(s => (
                          <SelectItem key={s} value={s} className="text-white hover:bg-navy-700">{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button type="submit" disabled={isSubmitting} className="w-full bg-blue-500 hover:bg-blue-600 text-white py-6">
                    {isSubmitting ? 'Submitting...' : 'Get Free Consultation'}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Who Should File */}
      <section data-testid="who-should-file" className="py-16 md:py-24 bg-navy-800/50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <span className="text-blue-400 font-medium mb-4 block">Eligibility</span>
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-white">Who Should File ITR?</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {whoShouldFile.map((item, i) => (
              <div key={i} data-testid={`who-${i}`} className="bg-navy-800 border border-white/5 rounded-2xl p-6 card-hover group">
                <div className="w-12 h-12 rounded-xl bg-blue-500/10 border border-blue-500/30 flex items-center justify-center mb-4">
                  <item.icon className="w-6 h-6 text-blue-400" />
                </div>
                <h3 className="font-heading font-semibold text-lg text-white mb-2">{item.title}</h3>
                <p className="text-slate-400 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tax Slabs */}
      <section data-testid="tax-slabs" className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <span className="text-blue-400 font-medium mb-4 block">Tax Rates FY 2024-25</span>
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-white">Income Tax Slab Rates</h2>
          </div>
          <div className="grid lg:grid-cols-2 gap-8">
            <div className="bg-navy-800 border border-white/5 rounded-2xl overflow-hidden">
              <div className="p-6 bg-blue-500/10 border-b border-white/5">
                <h3 className="font-heading font-semibold text-xl text-white">New Tax Regime</h3>
                <p className="text-slate-400 text-sm">No deductions (80C, HRA)</p>
              </div>
              <div className="p-6 space-y-3">
                {newSlabs.map((s, i) => (
                  <div key={i} className="flex justify-between py-2 border-b border-white/5 last:border-0">
                    <span className="text-slate-300">{s.range}</span>
                    <span className="text-blue-400 font-medium">{s.rate}</span>
                  </div>
                ))}
                <div className="mt-4 p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
                  <p className="text-green-400 text-sm font-medium">New Tax Regime Rebate: Income upto ₹12 Lac = No Tax</p>
                </div>
              </div>
            </div>
            <div className="bg-navy-800 border border-white/5 rounded-2xl overflow-hidden">
              <div className="p-6 bg-sky-500/10 border-b border-white/5">
                <h3 className="font-heading font-semibold text-xl text-white">Old Tax Regime</h3>
                <p className="text-slate-400 text-sm">Deductions allowed (80C, 80D, HRA)</p>
              </div>
              <div className="p-6 space-y-3">
                {oldSlabs.map((s, i) => (
                  <div key={i} className="flex justify-between py-2 border-b border-white/5 last:border-0">
                    <span className="text-slate-300">{s.range}</span>
                    <span className="text-sky-400 font-medium">{s.rate}</span>
                  </div>
                ))}
                <div className="mt-4 p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
                  <p className="text-green-400 text-sm">Rebate: Income ≤₹5L = No tax</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section data-testid="stats" className="py-16 md:py-24 bg-navy-800/50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((s, i) => (
              <div key={i} data-testid={`stat-${i}`} className="bg-navy-800 border border-white/5 rounded-2xl p-6 text-center">
                <div className="w-12 h-12 mx-auto rounded-xl bg-blue-500/10 flex items-center justify-center mb-4">
                  <s.icon className="w-6 h-6 text-blue-400" />
                </div>
                <p className="font-heading font-bold text-3xl text-blue-400">{s.value}</p>
                <p className="text-slate-400 text-sm">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Documents */}
      <section data-testid="documents" className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <span className="text-blue-400 font-medium mb-4 block">Documentation</span>
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-white">Documents Required</h2>
          </div>
          <div className="bg-navy-800 border border-white/5 rounded-2xl p-8">
            <div className="grid sm:grid-cols-2 gap-4">
              {documents.map((doc, i) => (
                <div key={i} className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0" />
                  <span className="text-slate-300">{doc}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Penalty Warning */}
      <section data-testid="penalty" className="py-16 md:py-24 bg-navy-800/50">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="bg-gradient-to-r from-red-500/10 to-orange-500/10 border border-red-500/20 rounded-2xl p-8">
            <div className="flex items-start gap-4">
              <AlertTriangle className="w-10 h-10 text-red-400 flex-shrink-0" />
              <div>
                <h2 className="font-heading font-bold text-2xl text-white mb-4">Penalties for Late Filing</h2>
                <ul className="space-y-2 text-slate-300">
                  <li>• Late fee: ₹1,000 to ₹5,000 based on income</li>
                  <li>• Interest: 1% per month on unpaid tax</li>
                  <li>• Notice from IT Department</li>
                  <li>• Loss of carry-forward losses benefit</li>
                </ul>
                <a href="#contact-form" className="inline-block mt-6">
                  <Button className="bg-blue-500 hover:bg-blue-600 text-white">
                    File ITR Now <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section data-testid="faqs" className="py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <span className="text-blue-400 font-medium mb-4 block">FAQs</span>
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-white">Frequently Asked Questions</h2>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <div key={i} data-testid={`faq-${i}`} className="bg-navy-800 border border-white/5 rounded-xl overflow-hidden">
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full px-6 py-5 flex items-center justify-between text-left">
                  <span className="font-heading font-semibold text-white pr-4">{faq.q}</span>
                  {openFaq === i ? <ChevronUp className="w-5 h-5 text-blue-400" /> : <ChevronDown className="w-5 h-5 text-blue-400" />}
                </button>
                {openFaq === i && <div className="px-6 pb-5"><p className="text-slate-400">{faq.a}</p></div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section data-testid="cta" className="py-16 md:py-24 bg-navy-800/50">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <div className="bg-gradient-to-r from-blue-500/10 to-sky-500/10 border border-white/10 rounded-3xl p-10 relative overflow-hidden">
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-4">Ready to File Your ITR?</h2>
            <p className="text-slate-400 mb-8">Expert CAs ensure accurate filing with maximum refunds.</p>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="#contact-form">
                <Button className="bg-blue-500 hover:bg-blue-600 text-white px-8 py-6 text-lg">
                  File ITR Now <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </a>
              <Link to="/contact">
                <Button variant="outline" className="border-white/20 text-white hover:bg-white/10 px-8 py-6 text-lg">Contact Us</Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
